import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';

const SLIDES = [
  {
    src: "https://storage.readdy-site.link/project_files/09ac3b88-05d8-4a69-af11-22c685bd56bb/d2e29780-8d50-4008-b09d-bbb63e7bb837_unnamed.jpg?v=06f1f1056eb1276aba496da949a12003",
    mobilePosition: "38% top",
  },
  {
    src: "https://storage.readdy-site.link/project_files/09ac3b88-05d8-4a69-af11-22c685bd56bb/bed8c1bf-2f0b-43d3-a777-e8c9230cb133_-.jpg?v=3a105b367a1f3e6bc571101187a18cc9",
    mobilePosition: "58% top",
  },
  {
    src: "https://storage.readdy-site.link/project_files/09ac3b88-05d8-4a69-af11-22c685bd56bb/e26d005e-0cc3-461b-920b-7f74f8a454d1_1.jpg?v=1aa015661fb1f0d06bfea7e16cc7878b",
    mobilePosition: "top center",
  },
];

const INTERVAL = 4500;

// ── Video Modal ──
function VideoModal({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full max-w-4xl mx-4 rounded-2xl overflow-hidden shadow-2xl"
        style={{ aspectRatio: '16/9' }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/80 transition-colors cursor-pointer"
          aria-label="关闭视频"
        >
          <i className="ri-close-line text-lg" />
        </button>
        <video
          className="w-full h-full object-cover"
          controls
          autoPlay
          playsInline
          src="https://storage.readdy-site.link/project_files/09ac3b88-05d8-4a69-af11-22c685bd56bb/dff154ea-4956-44d7-a5c3-d55dd561c40d_NIMO-Brand-video-V2-.mp4?v=48ed2bb0c10fb498acb0b40e500402b2"
          poster="https://readdy.ai/api/search-image?query=NIMO%20AI%20smart%20glasses%20technology%20product%20showcase%20cinematic%20dark%20background%20with%20soft%20light%20gradient%20premium%20minimalist%20aesthetic%20high%20quality%20video%20thumbnail&width=1280&height=720&seq=nimo-video-poster-01&orientation=landscape"
          {...({
            'x5-video-player-type': 'h5',
            'x5-video-player-fullscreen': 'false',
            'x5-playsinline': 'true',
            'webkit-playsinline': 'true',
          } as React.HTMLAttributes<HTMLVideoElement>)}
        >
          您的浏览器不支持视频播放
        </video>
      </div>
    </div>
  );
}

export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const [progressKey, setProgressKey] = useState(0);
  const [firstImgReady, setFirstImgReady] = useState(false);
  const [revealedSlides, setRevealedSlides] = useState<Set<number>>(new Set([0]));
  const { t } = useTranslation();
  const { currentLang } = useLanguage();

  const handleCta = useCallback(() => {
    if (currentLang === 'en') {
      const el = document.getElementById('preorder');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.open('https://j.youzan.com/26bIJk', '_blank', 'noopener,noreferrer');
    }
  }, [currentLang]);

  const parallaxBgRef = useRef<HTMLDivElement>(null);
  const parallaxTextRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const revealSlide = useCallback((index: number) => {
    setRevealedSlides(prev => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, []);

  const startInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrent(prev => {
          const next = (prev + 1) % SLIDES.length;
          revealSlide(next);
          return next;
        });
        setFading(false);
        setProgressKey(k => k + 1);
      }, 500);
    }, INTERVAL);
  }, [revealSlide]);

  const goTo = useCallback((index: number) => {
    revealSlide(index);
    setFading(true);
    setTimeout(() => {
      setCurrent(index);
      setFading(false);
      setProgressKey(k => k + 1);
    }, 500);
    startInterval();
  }, [startInterval, revealSlide]);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    startInterval();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startInterval]);

  useEffect(() => {
    const bg = parallaxBgRef.current;
    const txt = parallaxTextRef.current;
    if (!bg) return;

    const applyParallax = (y: number) => {
      bg.style.transform = `translateY(${y * 0.40}px)`;
      if (txt) txt.style.transform = `translateY(${y * 0.12}px)`;
    };

    applyParallax(window.scrollY);
    const onScroll = () => applyParallax(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <section
        id="home"
        data-dark-section
        className="relative w-full overflow-hidden bg-black"
        style={{ height: 'calc(var(--vh, 1vh) * 100)', minHeight: '700px' }}
      >
        {/* ── Parallax Background Layer ── */}
        <div
          ref={parallaxBgRef}
          className="absolute w-full will-change-transform"
          style={{ top: '-12%', height: '124%' }}
        >
          {SLIDES.map((slide, i) => (
            <div
              key={slide.src}
              className="absolute inset-0 transition-opacity duration-700"
              style={{
                opacity: i === current
                  ? (fading ? 0 : (i === 0 && !firstImgReady ? 0 : 1))
                  : 0,
              }}
            >
              {/* 只有当该 slide 被 reveal 后才渲染 img，节省首屏请求 */}
              {revealedSlides.has(i) && (
                <>
                  <img
                    src={slide.src}
                    alt={`NIMO 智能眼镜 ${i + 1}`}
                    className="lg:hidden w-full h-full object-cover"
                    style={{ objectPosition: slide.mobilePosition }}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    fetchpriority={i === 0 ? 'high' : 'low'}
                    decoding={i === 0 ? 'sync' : 'async'}
                    draggable={false}
                    onLoad={i === 0 ? () => setFirstImgReady(true) : undefined}
                  />
                  <img
                    src={slide.src}
                    alt={`NIMO 智能眼镜 ${i + 1}`}
                    className="hidden lg:block w-full h-full object-cover object-top"
                    loading={i === 0 ? 'eager' : 'lazy'}
                    fetchpriority={i === 0 ? 'high' : 'low'}
                    decoding={i === 0 ? 'sync' : 'async'}
                    draggable={false}
                    onLoad={i === 0 ? () => setFirstImgReady(true) : undefined}
                  />
                </>
              )}
            </div>
          ))}
        </div>

        {/* ── Overlays ── */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent lg:hidden" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/35 via-black/10 to-transparent hidden lg:block" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent hidden lg:block" />

        {/* ── Mobile Content ── */}
        <div className="lg:hidden absolute inset-0 z-10 flex flex-col justify-end px-6 md:px-14 pb-24 md:pb-32">
          <p
            className="font-label text-white/60 text-xs md:text-sm tracking-[0.3em] uppercase mb-4"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(16px)',
              filter: loaded ? 'blur(0px)' : 'blur(6px)',
              transition: 'opacity 0.8s ease 300ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) 300ms, filter 0.8s ease 300ms',
            }}
          >
            {t('hero_label')}
          </p>
          <div className="overflow-hidden mb-1">
            <h1
              className="font-zh-serif text-3xl md:text-5xl text-white font-light leading-tight"
              style={{
                display: 'block',
                transform: loaded ? 'translateY(0)' : 'translateY(110%)',
                transition: 'transform 1s cubic-bezier(0.16,1,0.3,1) 480ms',
              }}
            >
              {t('hero_line1')}
            </h1>
          </div>
          <div className="overflow-hidden mb-5">
            <h2
              className="font-zh-serif text-3xl md:text-5xl text-white/90 font-light leading-tight"
              style={{
                display: 'block',
                transform: loaded ? 'translateY(0)' : 'translateY(110%)',
                transition: 'transform 1s cubic-bezier(0.16,1,0.3,1) 630ms',
              }}
            >
              {t('hero_line2')}
            </h2>
          </div>
          <div className="mb-4 overflow-hidden h-px">
            <div
              className="h-px bg-white/50 origin-left"
              style={{
                transform: loaded ? 'scaleX(1)' : 'scaleX(0)',
                width: '2.5rem',
                transition: 'transform 0.9s cubic-bezier(0.16,1,0.3,1) 820ms',
              }}
            />
          </div>
          <p
            className="text-white/75 text-sm md:text-base font-light leading-relaxed max-w-xs md:max-w-md mb-7"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(14px)',
              filter: loaded ? 'blur(0px)' : 'blur(4px)',
              transition: 'opacity 0.9s ease 960ms, transform 0.9s cubic-bezier(0.16,1,0.3,1) 960ms, filter 0.9s ease 960ms',
            }}
          >
            {t('hero_desc')}
          </p>
          {/* ── QR Code (Mobile) ── */}
          <div
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? 'translateY(0)' : 'translateY(14px)',
              transition: 'opacity 0.9s ease 1100ms, transform 0.9s cubic-bezier(0.16,1,0.3,1) 1100ms',
            }}
            className="flex items-end gap-4"
          >
            <div className="rounded-xl overflow-hidden border border-white/20 bg-white/10 backdrop-blur-sm">
              <img
                src="https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/53089a4ea22bc7d8f7d75e5931ad62dc.png"
                alt="购买二维码"
                className="block max-w-[120px]"
              />
            </div>
            <div className="pb-1">
              <p className="text-white/90 text-sm font-medium tracking-wide mb-0.5">
                {currentLang === 'en' ? 'Scan to Buy' : '扫码购买'}
              </p>
              <p className="text-white/45 text-xs font-light tracking-wide">
                {currentLang === 'en' ? 'WeChat · Scan QR Code' : '微信扫一扫'}
              </p>
            </div>
          </div>
        </div>

        {/* ── Desktop Content ── */}
        <div className="hidden lg:flex relative z-10 w-full h-full flex-col justify-center">
          <div className="max-w-7xl mx-auto w-full px-6 lg:px-12">
            <div ref={parallaxTextRef} className="will-change-transform">
              <p
                className="font-label text-white/60 text-sm tracking-[0.3em] uppercase mb-5"
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(18px)',
                  filter: loaded ? 'blur(0px)' : 'blur(8px)',
                  transition: 'opacity 0.9s ease 200ms, transform 0.9s cubic-bezier(0.16,1,0.3,1) 200ms, filter 0.9s ease 200ms',
                }}
              >
                {t('hero_label')}
              </p>
              <div className="overflow-hidden mb-2">
                <h1
                  className="font-zh-serif text-5xl xl:text-6xl text-white font-light"
                  style={{
                    display: 'block',
                    lineHeight: 1.15,
                    transform: loaded ? 'translateY(0)' : 'translateY(105%)',
                    transition: 'transform 1.1s cubic-bezier(0.16,1,0.3,1) 400ms',
                  }}
                >
                  {t('hero_line1')}
                </h1>
              </div>
              <div className="overflow-hidden mb-7">
                <h2
                  className="font-zh-serif text-5xl xl:text-6xl text-white/90 font-light"
                  style={{
                    display: 'block',
                    lineHeight: 1.15,
                    transform: loaded ? 'translateY(0)' : 'translateY(105%)',
                    transition: 'transform 1.1s cubic-bezier(0.16,1,0.3,1) 570ms',
                  }}
                >
                  {t('hero_line2')}
                </h2>
              </div>
              <div className="mb-7 h-px overflow-hidden" style={{ width: '3.5rem' }}>
                <div
                  className="h-full bg-white/50 origin-left"
                  style={{
                    transform: loaded ? 'scaleX(1)' : 'scaleX(0)',
                    transition: 'transform 1s cubic-bezier(0.16,1,0.3,1) 780ms',
                  }}
                />
              </div>
              <p
                className="text-white/80 text-base font-light tracking-wide max-w-sm mb-10"
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(16px)',
                  filter: loaded ? 'blur(0px)' : 'blur(5px)',
                  transition: 'opacity 0.9s ease 950ms, transform 0.9s cubic-bezier(0.16,1,0.3,1) 950ms, filter 0.9s ease 950ms',
                }}
              >
                {t('hero_desc')}
              </p>

              {/* ── QR Code ── */}
              <div
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(16px)',
                  transition: 'opacity 0.9s ease 1100ms, transform 0.9s cubic-bezier(0.16,1,0.3,1) 1100ms',
                }}
                className="flex items-end gap-4"
              >
                <div className="relative group">
                  <div className="rounded-xl overflow-hidden border border-white/20 bg-white/10 backdrop-blur-sm transition-all duration-300 group-hover:border-white/50 group-hover:scale-105">
                    <img
                      src="https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/53089a4ea22bc7d8f7d75e5931ad62dc.png"
                      alt="购买二维码"
                      className="block max-w-[160px]"
                    />
                  </div>
                </div>
                <div className="pb-1">
                  <p className="text-white/90 text-sm font-medium tracking-wide mb-0.5">
                    {currentLang === 'en' ? 'Scan to Buy' : '扫码购买'}
                  </p>
                  <p className="text-white/45 text-xs font-light tracking-wide">
                    {currentLang === 'en' ? 'WeChat · Scan QR Code' : '微信扫一扫'}
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ── Carousel Progress Indicators ── */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="relative h-0.5 rounded-full overflow-hidden cursor-pointer"
              style={{
                width: i === current ? '48px' : '24px',
                transition: 'width 0.3s cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              <div className="absolute inset-0 bg-white/30 rounded-full" />
              {i === current ? (
                <div
                  key={`p-${progressKey}`}
                  className="absolute inset-y-0 left-0 bg-white rounded-full"
                  style={{
                    animation: `heroProgress ${INTERVAL}ms linear forwards`,
                  }}
                />
              ) : i < current ? (
                <div className="absolute inset-0 bg-white rounded-full" />
              ) : null}
            </button>
          ))}
        </div>
      </section>
    </>
  );
}
